import { useCallback } from 'react'

import { Post, PostStatus, Space } from '@tribeplatform/gql-client/types'
import { findPostType } from '@tribeplatform/react-components/CMS'
import { HIGHLIGHTED_POST_QUERY_PARAM } from '@tribeplatform/react-components/common/constants'
import { getPostIdFromPath } from '@tribeplatform/react-components/common/utils'
import { useI18n } from '@tribeplatform/react-components/i18n'
import { PostForm } from '@tribeplatform/react-components/PostForm'
import { useRouter } from '@tribeplatform/react-sdk'
import { useNetwork, usePost, useSpace } from '@tribeplatform/react-sdk/hooks'
import { IconButton } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'

import { StatusBadge } from './StatusBadge.js'
import {
  getTranslatedUpdatePostText,
  getTranslatedCreatePostText,
} from './translations.js'

interface Props {
  selectedSpace?: Space
  isNew: boolean
  postPath: string
  postTypeSlug?: string
  onCancel: () => void
}

export const PostComposer = ({
  isNew,
  postPath,
  postTypeSlug,
  onCancel,
  selectedSpace,
  ...rest
}: Props) => {
  const intl = useI18n()
  const { replace } = useRouter()

  const {
    data: { id: networkId, postTypes: networkPostTypes },
  } = useNetwork()

  const postId = postPath ? getPostIdFromPath(postPath) : null
  const { data: post, isFetched } = usePost({
    variables: { id: postId },
    useQueryOptions: { enabled: !!postId },
  })

  const postType = findPostType({
    networkId,
    id: post?.postTypeId,
    slug: postTypeSlug,
    postTypes: networkPostTypes,
  })

  const maybeSpaceId =
    selectedSpace?.id ?? postType?.authMemberProps?.availableSpaces?.[0]?.id

  const { data: space } = useSpace({
    variables: { id: maybeSpaceId },
    useQueryOptions: { enabled: !!maybeSpaceId },
  })

  const onPostSubmitted = useCallback(
    (post: Post) => {
      if (post?.status === PostStatus.DRAFT && !!post?.scheduledFor) {
        replace(
          `/settings/moderation/all/scheduled-posts?${HIGHLIGHTED_POST_QUERY_PARAM}=${post?.id}`,
        )
      } else if (post?.status === PostStatus.DRAFT) {
        replace('/settings/moderation/all/draft-posts')
      } else {
        replace(post?.relativeUrl)
      }
    },
    [replace],
  )

  if (!isNew && !post) {
    return null
  }

  const postTypeTitle = post?.id
    ? getTranslatedUpdatePostText(intl, postType?.name)
    : getTranslatedCreatePostText(intl, postType?.name)

  return (
    <Card {...rest}>
      <Card.Header
        title={
          <div className="flex space-s-3 items-center">
            <IconButton
              variant="tertiaryNeutral"
              rounded
              size="lg"
              icon={<SvgIcon className="rtl:-scale-x-100" name="arrow-left" />}
              onClick={onCancel}
              aria-label={intl.$t({
                id: 'Generics.Back',
                defaultMessage: 'Back',
              })}
            />
            <span className="text-xl font-medium flex-1">{postTypeTitle}</span>
            <StatusBadge
              status={post?.status}
              scheduledFor={post?.scheduledFor}
            />
          </div>
        }
      />
      <Card.Content>
        <PostForm
          postType={postType}
          space={space}
          availableSpaces={postType?.authMemberProps?.availableSpaces}
          post={post}
          isPostReady={isFetched}
          onCancel={onCancel}
          onSubmit={onPostSubmitted}
        />
      </Card.Content>
    </Card>
  )
}
